/**
 * Targeted Pinia → Sentry state scrub: only customer-domain stores registered below,
 * using Zod `meta()` PII registry via {@link redactPiiSafe}.
 *
 * Other stores pass through unchanged (no generic deep scrub).
 */
import { z, type ZodType } from 'zod'
import {
  availabilitySlotCustomerDraftsSchema,
  availabilitySlotsSchema,
  clinicalCustomerDraftEntrySchema,
  classInstanceCustomerNoticeSchema,
  customerRecordingBookmarkSchema,
  customerSchema,
  instructorSchema,
  meta,
  patientInformationSurveySchema,
  paymentMethodInputCacheDocumentSchema,
  PiiCategory,
  sponsorInfoSchema,
  getFieldMeta,
  subscriptionScheduleSchema,
  subscriptionSchema,
  userAssessmentSurveySchema,
} from '@gh/common/models'
import { getPiiFields, redactPiiSafe } from '@gh/common/utils'
import createLogger from './logger'

const logger = createLogger('sentryPiniaStoreScrub')

/** Store ids must match Pinia `defineStore('...')` ids. */
export const CUSTOMER_DOMAIN_PINIA_SCRUB_STORE_IDS = [
  'customer',
  'subscription',
  'subscriptionSchedule',
  'sponsorInfo',
  'signup',
  'recordingBookmarks',
  'classInstanceCustomerNotices',
  'instructors',
  'tellescopeAvailability',
  'clinicalTellescopeAvailability',
  'paymentMethod',
  'FirebaseMessaging',
  'zoomRegistrations',
] as const

export type CustomerDomainPiniaScrubStoreId = (typeof CUSTOMER_DOMAIN_PINIA_SCRUB_STORE_IDS)[number]

export type PiniaScrubSliceKind = 'document' | 'array' | 'mapValues'

export type PiniaScrubTarget = {
  sliceKey: string
  schema: ZodType
  kind?: PiniaScrubSliceKind
}

type CustomerDomainRegistryEntry = {
  targets: PiniaScrubTarget[]
  /** True when any target schema has PII meta paths. */
  hasPii: boolean
}

/** Zoom registration cache held in Pinia (not the Firestore webinarRegistrant doc). */
const zoomRegistrationStateScrubSchema = z.object({
  status: z.enum(['pending', 'completed', 'error']),
  joinUrl: meta(z.string().optional(), { pii: { category: PiiCategory.Url } }),
  meetingNumber: z.string().optional(),
  registrantToken: meta(z.string().optional(), {
    pii: { category: PiiCategory.Credential },
  }),
  errorMessage: meta(z.string().optional(), { pii: { category: PiiCategory.FreeText } }),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
})

const fcmRegistrationTokenScrubSchema = meta(z.string().nullable(), {
  pii: { category: PiiCategory.Credential },
})

function schemaHasPii(schema: ZodType): boolean {
  return getPiiFields(schema).length > 0 || Boolean(getFieldMeta(schema)?.pii)
}

function makeRegistryEntry(targets: PiniaScrubTarget[]): CustomerDomainRegistryEntry {
  const hasPii = targets.some((t) => schemaHasPii(t.schema))
  return { targets, hasPii }
}

/**
 * Registry of customer-related stores and the state slices that map to Zod models with PII meta.
 * Extend when adding customer-domain Pinia stores that mirror Firestore models or hold PHI locally.
 */
export const PINIA_CUSTOMER_DOMAIN_SCRUB_REGISTRY: Record<
  CustomerDomainPiniaScrubStoreId,
  CustomerDomainRegistryEntry
> = {
  customer: makeRegistryEntry([{ sliceKey: 'customer', schema: customerSchema }]),
  subscription: makeRegistryEntry([{ sliceKey: 'subscription', schema: subscriptionSchema }]),
  subscriptionSchedule: makeRegistryEntry([
    { sliceKey: 'subscriptionSchedule', schema: subscriptionScheduleSchema },
  ]),
  sponsorInfo: makeRegistryEntry([{ sliceKey: 'sponsorInfo', schema: sponsorInfoSchema }]),
  signup: makeRegistryEntry([
    { sliceKey: 'patientInformationSurvey', schema: patientInformationSurveySchema },
    { sliceKey: 'userAssessmentSurvey', schema: userAssessmentSurveySchema },
  ]),
  recordingBookmarks: makeRegistryEntry([
    {
      sliceKey: 'bookmarksMap',
      schema: customerRecordingBookmarkSchema,
      kind: 'mapValues',
    },
  ]),
  classInstanceCustomerNotices: makeRegistryEntry([
    {
      sliceKey: 'notices',
      schema: classInstanceCustomerNoticeSchema,
      kind: 'array',
    },
  ]),
  instructors: makeRegistryEntry([
    { sliceKey: 'instructors', schema: instructorSchema, kind: 'array' },
  ]),
  tellescopeAvailability: makeRegistryEntry([
    { sliceKey: 'tellescopeAvailability', schema: availabilitySlotsSchema },
    {
      sliceKey: 'tellescopeAvailabilityDraftSelects',
      schema: availabilitySlotCustomerDraftsSchema,
    },
  ]),
  clinicalTellescopeAvailability: makeRegistryEntry([
    { sliceKey: 'tellescopeAvailability', schema: availabilitySlotsSchema },
    {
      sliceKey: 'clinicalCustomerDraftSelects',
      schema: clinicalCustomerDraftEntrySchema,
      kind: 'mapValues',
    },
  ]),
  paymentMethod: makeRegistryEntry([
    { sliceKey: 'cacheDoc', schema: paymentMethodInputCacheDocumentSchema },
  ]),
  FirebaseMessaging: makeRegistryEntry([
    { sliceKey: 'registrationToken', schema: fcmRegistrationTokenScrubSchema },
    { sliceKey: 'lastSavedToken', schema: fcmRegistrationTokenScrubSchema },
  ]),
  zoomRegistrations: makeRegistryEntry([
    {
      sliceKey: 'registrations',
      schema: zoomRegistrationStateScrubSchema,
      kind: 'mapValues',
    },
  ]),
}

function scrubSliceValue(value: unknown, schema: ZodType, kind: PiniaScrubSliceKind): unknown {
  if (value === null || value === undefined) {
    return value
  }

  if (kind === 'document') {
    return redactPiiSafe(value, schema)
  }

  if (kind === 'array') {
    if (!Array.isArray(value)) {
      return value
    }
    return value.map((item) => redactPiiSafe(item, schema))
  }

  if (value instanceof Map) {
    const out = new Map<unknown, unknown>()
    for (const [key, entry] of value.entries()) {
      out.set(key, redactPiiSafe(entry, schema))
    }
    return out
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactPiiSafe(item, schema))
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      out[key] = redactPiiSafe(entry, schema)
    }
    return out
  }

  return value
}

/**
 * Applies meta-registry PII redaction only to registered customer-domain store slices.
 * Unregistered store ids and slices without PII metadata are left unchanged.
 */
export function scrubPiniaStateForSentryCustomerStores(state: unknown): unknown {
  const t0 = performance.now()
  const storeTimingsMs: Partial<Record<CustomerDomainPiniaScrubStoreId, number>> = {}

  if (state === null || typeof state !== 'object' || Array.isArray(state)) {
    return state
  }

  const root = state as Record<string, unknown>
  const out: Record<string, unknown> = { ...root }

  for (const storeId of CUSTOMER_DOMAIN_PINIA_SCRUB_STORE_IDS) {
    const registryEntry = PINIA_CUSTOMER_DOMAIN_SCRUB_REGISTRY[storeId]
    if (!registryEntry.hasPii) {
      continue
    }

    const storeState = out[storeId]
    if (storeState === null || typeof storeState !== 'object' || Array.isArray(storeState)) {
      continue
    }

    const rec = storeState as Record<string, unknown>
    const storeT0 = performance.now()
    let storeChanged = false
    const nextStore: Record<string, unknown> = { ...rec }

    for (const target of registryEntry.targets) {
      if (!schemaHasPii(target.schema)) {
        continue
      }

      const slice = rec[target.sliceKey]
      if (slice === null || slice === undefined) {
        continue
      }

      nextStore[target.sliceKey] = scrubSliceValue(slice, target.schema, target.kind ?? 'document')
      storeChanged = true
    }

    if (storeChanged) {
      storeTimingsMs[storeId] = Math.round(performance.now() - storeT0)
      out[storeId] = nextStore
    }
  }

  const durationMs = Math.round(performance.now() - t0)
  if (Object.keys(storeTimingsMs).length > 0) {
    logger.log('scrubPiniaStateForSentryCustomerStores', { durationMs, storeTimingsMs })
  }

  return out
}
